/* eslint-disable @next/next/no-img-element */
import React, { createContext, useContext, useMemo } from 'react'

import { resolveBrandingIconSet } from '@/lib/branding-icons'
import { getTenantMediaDefaults, resolveDefaultFaviconUrl } from '@/lib/tenant-media-defaults'
import { useTenantConfig } from '@/hooks/use-tenant-config'

import { PrimaryIcon, PrimaryIconV2 } from '@/components/icons'

import { resolveDomainBranding } from '@/utils/domain-branding-resolver'
import { normalizeTenantDisplayName } from '@/utils/tenant.utils'

export interface BrandingOverrideValue {
  brandHref?: string
  fallbackLogoUrl?: string
  brandName?: string
}

const BrandingHostnameContext = createContext<string>('')

interface BrandingResult {
  logoElement: React.ReactNode
  logoElementSmall: React.ReactNode
  /** True when nav/footer should render a custom or tenant image URL (not the FSDS icon component). */
  hasImageLogo: boolean
  logoUrl: string
  siteName: string
  copyrightHolder: string
  brandHref: string
  appTitle: string
  publisherName: string
  iconSet: {
    ico: string
    png16: string
    png32: string
    appleTouchIcon: string
    manifest: string
  }
  seoMeta: {
    defaultDescription: string
    defaultKeywords: string
    twitterHandle: string
    themeColor: string
    locale: string
    language: string
    geoRegion: string
    geoPlacename: string
  }
  isFsdsDomain: boolean
  hasTenantBranding: boolean
}

const BrandingOverrideContext = createContext<BrandingOverrideValue | null>(null)

function BrandingImage({ src, alt, size = 'nav' }: { src: string; alt: string; size?: 'nav' | 'footer' }) {
  return (
    <img
      src={src}
      alt={alt}
      className={size === 'nav' ? 'h-8 w-auto max-w-48 object-contain' : 'h-6 w-auto max-w-40 object-contain'}
    />
  )
}

export function BrandingOverrideProvider({
  children,
  value,
}: {
  children: React.ReactNode
  value: BrandingOverrideValue
}) {
  return <BrandingOverrideContext.Provider value={value}>{children}</BrandingOverrideContext.Provider>
}

export function BrandingHostnameProvider({ children, hostname }: { children: React.ReactNode; hostname: string }) {
  return <BrandingHostnameContext.Provider value={hostname}>{children}</BrandingHostnameContext.Provider>
}

function resolveBrandingDisplayState({
  isFsdsDomain,
  resolvedBrandName,
  resolvedBrandHref,
  resolvedLogoUrl,
  resolvedMeta,
  resolvedIcons,
  tenantName,
  tenantLogo,
  overrideBrandName,
  overrideBrandHref,
  overrideLogoUrl,
}: {
  isFsdsDomain: boolean
  resolvedBrandName: string
  resolvedBrandHref: string
  resolvedLogoUrl?: string
  resolvedMeta: {
    appTitle: string
    publisherName: string
    defaultDescription: string
    defaultKeywords: string
    twitterHandle: string
    themeColor: string
    locale: string
    language: string
    geoRegion: string
    geoPlacename: string
  }
  resolvedIcons: BrandingResult['iconSet']
  tenantName: string
  tenantLogo?: string
  overrideBrandName?: string
  overrideBrandHref?: string
  overrideLogoUrl?: string
}): Omit<BrandingResult, 'logoElement' | 'logoElementSmall' | 'logoUrl' | 'hasImageLogo'> & {
  effectiveLogo: string | null
} {
  const hasTenantBranding = Boolean(tenantName || tenantLogo)

  if (isFsdsDomain) {
    return {
      siteName: resolvedBrandName,
      copyrightHolder: resolvedBrandName,
      brandHref: overrideBrandHref || resolvedBrandHref,
      appTitle: resolvedMeta.appTitle,
      publisherName: resolvedMeta.publisherName,
      iconSet: resolvedIcons,
      seoMeta: {
        defaultDescription: resolvedMeta.defaultDescription,
        defaultKeywords: resolvedMeta.defaultKeywords,
        twitterHandle: resolvedMeta.twitterHandle,
        themeColor: resolvedMeta.themeColor,
        locale: resolvedMeta.locale,
        language: resolvedMeta.language,
        geoRegion: resolvedMeta.geoRegion,
        geoPlacename: resolvedMeta.geoPlacename,
      },
      isFsdsDomain: true,
      hasTenantBranding,
      effectiveLogo: null,
    }
  }

  const siteName =
    overrideBrandName || (tenantName ? normalizeTenantDisplayName(tenantName) : '') || resolvedBrandName

  return {
    siteName,
    copyrightHolder: siteName,
    brandHref: overrideBrandHref || resolvedBrandHref,
    appTitle: siteName,
    publisherName: siteName,
    iconSet: resolvedIcons,
    seoMeta: {
      defaultDescription: resolvedMeta.defaultDescription,
      defaultKeywords: resolvedMeta.defaultKeywords,
      twitterHandle: resolvedMeta.twitterHandle,
      themeColor: resolvedMeta.themeColor,
      locale: resolvedMeta.locale,
      language: resolvedMeta.language,
      geoRegion: resolvedMeta.geoRegion,
      geoPlacename: resolvedMeta.geoPlacename,
    },
    isFsdsDomain: false,
    hasTenantBranding,
    effectiveLogo: overrideLogoUrl || tenantLogo || resolvedLogoUrl || null,
  }
}

export function useBrandingHostname(): string {
  return useContext(BrandingHostnameContext)
}

export function useBranding(): BrandingResult {
  const { logo, tenantName, customization } = useTenantConfig()
  const override = useContext(BrandingOverrideContext)
  const hostname = useBrandingHostname()

  return useMemo(() => {
    const runtimeHostname =
      typeof window !== 'undefined' && window.location.hostname ? window.location.hostname : hostname
    const resolvedBranding = resolveDomainBranding(runtimeHostname)
    const resolvedIcons = resolveBrandingIconSet(resolvedBranding.icons, resolveDefaultFaviconUrl(customization))
    const configuredLogoUrl =
      getTenantMediaDefaults(customization).defaultLogoUrl?.trim() || logo?.trim() || undefined
    const brandingState = resolveBrandingDisplayState({
      isFsdsDomain: resolvedBranding.isFsdsDomain,
      resolvedBrandName: resolvedBranding.brandName,
      resolvedBrandHref: resolvedBranding.brandHref,
      resolvedLogoUrl: resolvedBranding.logoUrl,
      resolvedMeta: resolvedBranding.meta,
      resolvedIcons,
      tenantName: tenantName.trim(),
      tenantLogo: configuredLogoUrl,
      overrideBrandName: override?.brandName?.trim(),
      overrideBrandHref: override?.brandHref?.trim(),
      overrideLogoUrl: override?.fallbackLogoUrl?.trim(),
    })
    const logoAlt = brandingState.siteName ? `${brandingState.siteName} logo` : 'Brand logo'

    if (brandingState.effectiveLogo) {
      return {
        ...brandingState,
        hasImageLogo: true,
        logoUrl: brandingState.effectiveLogo,
        logoElement: <BrandingImage src={brandingState.effectiveLogo} alt={logoAlt} size="nav" />,
        logoElementSmall: <BrandingImage src={brandingState.effectiveLogo} alt={logoAlt} size="footer" />,
      }
    }

    return {
      ...brandingState,
      hasImageLogo: false,
      logoUrl: resolvedBranding.icons.ico,
      logoElement: <PrimaryIconV2 />,
      logoElementSmall: <PrimaryIcon />,
    }
  }, [hostname, logo, customization, override?.brandHref, override?.brandName, override?.fallbackLogoUrl, tenantName])
}
